import { type ToolMessage } from '@ag-ui/core';
import { useMemo } from 'react';
import { z } from 'zod';

import { useAgentManager } from '../providers/AgentProvider';
import { type ToolCallStatusEntry } from '../store/slices/createToolCallStatusSlice';
import { useThreadMessages } from '../store/useChatStore';
import { useToolCallStatusForThread } from '../store/useToolCallStatusStore';
import { parseToolContent } from '../tools';
import { isAgentMediaInventoryMessage } from '../utils/isAgentMediaInventoryMessage';
import {
  type DisplayMessage,
  type EnrichedToolMessage,
  getGroupStatus,
  isEnrichedToolMessage,
  type ToolCallStatus,
} from './messageTypes';

const TOOL_ARGS_DESCRIPTION_SCHEMA = z.object({ description: z.string() });

/**
 * Extracts a localized tool description from the tool call's JSON arguments.
 * Returns null when the field is absent, the value is not a string, or
 * the arguments are incomplete JSON (common during streaming).
 */
const extractToolDescription = (args: string): string | null => {
  try {
    return TOOL_ARGS_DESCRIPTION_SCHEMA.parse(JSON.parse(args)).description;
  } catch {
    // Incomplete JSON during streaming, missing field, or wrong type
  }

  return null;
};

/** Return type for useMessages hook */
export type UseMessagesReturn = {
  /** All messages with tool messages enriched with tool name info */
  messages: DisplayMessage[];
  /** Messages filtered for display (excludes completed tool-call-only assistant messages) */
  displayMessages: DisplayMessage[];
  /** Lookup map for toolCallId -> function name */
  toolCallMap: Map<string, string>;
};

/**
 * Hook that processes chat messages for display purposes.
 */
export const useMessages = (): UseMessagesReturn => {
  const { currentThreadId } = useAgentManager();
  const rawMessages = useThreadMessages(currentThreadId);
  const toolCallStatuses = useToolCallStatusForThread(currentThreadId);

  return useMemo(() => {
    const toolCallMap = new Map<string, string>();
    const toolDescriptionMap = new Map<string, string>();

    for (const message of rawMessages) {
      if (message.role === 'assistant' && message.toolCalls) {
        for (const toolCall of message.toolCalls) {
          toolCallMap.set(toolCall.id, toolCall.function.name);
          const description = extractToolDescription(toolCall.function.arguments);

          if (description) {
            toolDescriptionMap.set(toolCall.id, description);
          }
        }
      }
    }

    const completedToolCallIds = new Set<string>();

    for (const message of rawMessages) {
      if (message.role === 'tool') {
        completedToolCallIds.add(message.toolCallId);
      }
    }

    // Build a status lookup from the store
    const statusMap = new Map<string, ToolCallStatusEntry>();
    for (const entry of toolCallStatuses) {
      statusMap.set(entry.toolCallId, entry);
    }

    const enrichedMessages: DisplayMessage[] = rawMessages.map((message) => {
      if (message.role === 'tool') {
        const hasError = Boolean(message.error);
        const statusEntry = statusMap.get(message.toolCallId);
        const toolName = toolCallMap.get(message.toolCallId) ?? null;
        const status: ToolCallStatus =
          hasError || statusEntry?.status === 'error' ? 'error' : (statusEntry?.status ?? 'completed');

        return {
          ...message,
          hasError,
          parsedContent: parseToolContent(toolName, message.content),
          status,
          toolDescription: toolDescriptionMap.get(message.toolCallId) ?? null,
          toolName,
        };
      }

      return message;
    });

    // Inject synthetic entries for tool calls that are 'working' but have no ToolMessage yet
    const messageToolCallIds = new Set(
      rawMessages.filter((m): m is ToolMessage => m.role === 'tool').map((m) => m.toolCallId),
    );

    for (const entry of toolCallStatuses) {
      if ((entry.status === 'working' || entry.status === 'error') && !messageToolCallIds.has(entry.toolCallId)) {
        enrichedMessages.push({
          content: '',
          hasError: entry.status === 'error',
          id: `synthetic-${entry.toolCallId}`,
          parsedContent: null,
          role: 'tool',
          status: entry.status,
          toolCallId: entry.toolCallId,
          toolDescription: toolDescriptionMap.get(entry.toolCallId) ?? null,
          toolName: entry.toolCallName,
        });
      }
    }

    // Group consecutive tool messages into a single display item,
    // skipping empty assistant messages
    const displayMessages: DisplayMessage[] = [];
    let currentToolGroup: EnrichedToolMessage[] = [];

    const flushGroup = () => {
      if (currentToolGroup.length === 0) {
        return;
      }

      if (currentToolGroup.length === 1) {
        displayMessages.push(currentToolGroup[0]);
      } else {
        displayMessages.push({
          id: `tool-group-${currentToolGroup[0].id}`,
          messages: currentToolGroup,
          role: 'tool-group',
          status: getGroupStatus(currentToolGroup),
        });
      }

      currentToolGroup = [];
    };

    for (const message of enrichedMessages) {
      // A programmatic transform is sent as a system message (never rendered); treat it as a
      // run boundary so a new run's steps never merge into the previous run's group, even
      // before that run emits its file-edit result.
      if (message.role === 'system') {
        flushGroup();
        continue;
      }

      if (message.role === 'assistant' && !message.content) {
        continue;
      }

      if (
        message.role === 'assistant' &&
        typeof message.content === 'string' &&
        isAgentMediaInventoryMessage(message.content)
      ) {
        continue;
      }

      if (isEnrichedToolMessage(message)) {
        currentToolGroup.push(message);
      } else {
        flushGroup();
        displayMessages.push(message);
      }
    }
    flushGroup();

    const messages = enrichedMessages.filter((message) => message.role !== 'system');

    return { displayMessages, messages, toolCallMap };
  }, [rawMessages, toolCallStatuses]);
};
